<template>
    <div
        class="flex overflow-hidden rounded-lg border border-taupe-200 bg-white"
        data-testid="context-rule-card"
    >
        <div
            class="flex items-center p-2.5 text-white"
            :class="{
                'bg-taupe-200': isCreateMode || isUpdateMode,
                'bg-amber-400/80':
                    isCurrentTargetValue && !isCreateMode && !isUpdateMode,
                'bg-sky-500':
                    !isCurrentTargetValue && !isCreateMode && !isUpdateMode,
            }"
        >
            <zen-tooltip
                v-if="
                    !isCurrentTargetValue &&
                    !isCreateMode &&
                    !isUpdateMode &&
                    !isDraggable
                "
                content="This environment is not targeting this value, so this rule will be evaluated."
                placement="right"
                v-slot="{ setReference, ariaAttrs, show, hide }"
            >
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke-width="2"
                    stroke="currentColor"
                    class="h-5 w-5"
                    tabindex="0"
                    :ref="setReference"
                    v-bind="ariaAttrs"
                    @mouseenter="show"
                    @mouseleave="hide"
                    @focus="show"
                    @blur="hide"
                >
                    <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M4.5 12.75l7.5-7.5 7.5 7.5m-15 6l7.5-7.5 7.5 7.5"
                    />
                </svg>
            </zen-tooltip>
            <zen-tooltip
                v-if="
                    isCurrentTargetValue &&
                    !isCreateMode &&
                    !isUpdateMode &&
                    !isDraggable
                "
                content="The environment is already targeting this value, so this rule will not be evaluated."
                placement="right"
                v-slot="{ setReference, ariaAttrs, show, hide }"
            >
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke-width="2"
                    stroke="currentColor"
                    class="h-5 w-5"
                    tabindex="0"
                    :ref="setReference"
                    v-bind="ariaAttrs"
                    @mouseenter="show"
                    @mouseleave="hide"
                    @focus="show"
                    @blur="hide"
                >
                    <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M19.5 5.25l-7.5 7.5-7.5-7.5m15 6l-7.5 7.5-7.5-7.5"
                    />
                </svg>
            </zen-tooltip>
            <div
                v-if="isDraggable && !isCreateMode && !isUpdateMode"
                class="text-white cursor-grab handle"
            >
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke-width="1.5"
                    stroke="currentColor"
                    class="h-5 w-5"
                >
                    <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M3.75 9h16.5m-16.5 6.75h16.5"
                    />
                </svg>
            </div>
            <div v-if="isCreateMode || isUpdateMode" class="w-5"></div>
        </div>

        <!-- @container: the editor's columns follow the card's own width, not the viewport's, because the card sits beside the app navigation and is far narrower than the screen. -->
        <div
            v-if="isCreateMode || isUpdateMode"
            class="@container min-w-0 flex-1 p-3"
        >
            <div class="space-y-3">
                <div
                    class="flex flex-col gap-2 md:flex-row md:items-start md:justify-between"
                >
                    <div class="space-y-1.5">
                        <div class="flex flex-wrap items-center gap-2">
                            <div
                                class="text-sm font-medium text-taupe-900 sm:text-base"
                            >
                                {{
                                    isCreateMode
                                        ? 'New Target Rule'
                                        : modals.update.data.description ||
                                          'Edit Target Rule'
                                }}
                            </div>
                            <span
                                class="inline-flex items-center rounded-full bg-taupe-100 px-2 py-0.5 text-[11px] font-medium text-taupe-700 ring-1 ring-taupe-200"
                            >
                                {{ isCreateMode ? 'Creating' : 'Editing' }}
                            </span>
                        </div>
                        <p class="text-xs text-taupe-500 sm:text-sm">
                            Update the rule details and target value using the
                            same structure as the published rule card.
                        </p>
                    </div>

                    <div
                        class="flex shrink-0 items-center gap-1 rounded-md p-0.5 self-start"
                    >
                        <zen-button
                            v-if="isCreateMode"
                            type="button"
                            variant="primary"
                            size="small"
                            @click="confirmCreate"
                        >
                            Create
                        </zen-button>
                        <zen-button
                            v-if="isUpdateMode"
                            type="button"
                            variant="primary"
                            size="small"
                            @click="confirmUpdate"
                        >
                            Update
                        </zen-button>
                        <zen-button
                            type="button"
                            variant="secondary"
                            size="small"
                            @click="
                                isCreateMode ? cancelCreate() : toggleViewMode()
                            "
                        >
                            Cancel
                        </zen-button>
                    </div>
                </div>

                <!-- One column until the card is wide enough to give both boxes room: Rule Setup first, Served Value under it. The explicit minmax(0, 1fr) track lets a long value wrap inside its box instead of widening the column past the card. -->
                <div
                    class="grid grid-cols-[minmax(0,1fr)] gap-2.5 @4xl:grid-cols-[minmax(0,1.65fr)_minmax(14rem,0.95fr)]"
                >
                    <div
                        class="min-w-0 rounded-md border border-taupe-200 bg-white p-3 text-taupe-800"
                        data-testid="rule-editor-setup"
                    >
                        <div
                            class="text-[11px] font-medium uppercase tracking-[0.16em] text-taupe-500"
                        >
                            Rule Setup
                        </div>

                        <div class="mt-3 space-y-3">
                            <div class="space-y-1.5">
                                <label
                                    class="text-sm font-medium text-taupe-800"
                                >
                                    Description
                                </label>
                                <input
                                    type="text"
                                    class="block w-full rounded-md border-taupe-300 px-3 py-1.5 text-sm text-taupe-800 focus:border-sky-500 focus:ring-sky-500"
                                    v-model="modals.update.data.description"
                                    placeholder="Target Rule Description"
                                />
                            </div>

                            <div class="space-y-1.5">
                                <label
                                    class="text-sm font-medium text-taupe-800"
                                >
                                    Criteria
                                </label>
                                <!-- Each field has a minimum width and the row wraps when they no longer fit, so none of them is squeezed to an ellipsis. The value field grows the most. -->
                                <div class="flex flex-wrap gap-2.5">
                                    <div class="min-w-0 flex-[1_1_10rem]">
                                        <zen-select
                                            v-model="
                                                modals.update.data.criteria
                                                    .selector
                                            "
                                            :options="selectorList"
                                            option-label="name"
                                            option-value="value"
                                            @change="changeSelector"
                                        />
                                    </div>
                                    <div
                                        v-if="
                                            modals.update.data.criteria
                                                .selector == 'attribute'
                                        "
                                        class="min-w-0 flex-[1_1_10rem]"
                                    >
                                        <zen-auto-complete
                                            v-model="
                                                modals.update.data.criteria
                                                    .sub_selector
                                            "
                                            placeholder="Attribute"
                                            :options="
                                                modals.update.attributes.raw
                                            "
                                            :forceSelection="false"
                                            @change="changeAttribute"
                                        />
                                    </div>
                                    <div
                                        class="min-w-0 flex-[1_1_8rem]"
                                        data-testid="rule-editor-comparer"
                                    >
                                        <zen-select
                                            v-model="
                                                modals.update.data.criteria
                                                    .comparer
                                            "
                                            :options="comparerList"
                                            option-label="name"
                                            option-value="value"
                                            @change="changeComparer"
                                        />
                                    </div>
                                    <div
                                        v-if="
                                            modals.update.data.criteria
                                                .comparer != 'isnull' &&
                                            modals.update.data.criteria
                                                .comparer != 'notnull'
                                        "
                                        class="min-w-0 flex-[3_1_10rem]"
                                    >
                                        <input
                                            v-if="
                                                showManualMultipleAttributeValueInput
                                            "
                                            v-model="
                                                manualMultipleAttributeValues
                                            "
                                            type="text"
                                            placeholder="Values (comma-separated)"
                                            class="block w-full rounded-md border-taupe-300 px-3 py-1.5 text-sm text-taupe-800 focus:border-sky-500 focus:ring-sky-500"
                                        />
                                        <zen-auto-complete
                                            v-else
                                            v-model="
                                                modals.update.data.criteria
                                                    .values
                                            "
                                            placeholder="Value"
                                            :multiple="allowMultiple"
                                            :options="modals.update.values.raw"
                                            :forceSelection="
                                                modals.update.data.criteria
                                                    .selector === 'segment'
                                            "
                                            :optionLabel="
                                                modals.update.data.criteria
                                                    .selector === 'attribute'
                                                    ? 'value'
                                                    : modals.update.data
                                                            .criteria
                                                            .selector ===
                                                        'context'
                                                      ? 'identifier'
                                                      : 'name'
                                            "
                                            :filterLabel="
                                                modals.update.data.criteria
                                                    .selector === 'attribute'
                                                    ? 'value'
                                                    : modals.update.data
                                                            .criteria
                                                            .selector ===
                                                        'context'
                                                      ? 'identifier'
                                                      : 'name'
                                            "
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div
                        class="min-w-0 rounded-md border border-taupe-200 bg-white p-3"
                        data-testid="rule-editor-served-value"
                    >
                        <div
                            class="text-[11px] font-medium uppercase tracking-[0.16em] text-taupe-500"
                        >
                            Served Value
                        </div>
                        <div class="mt-3">
                            <boolean-target-toggle
                                v-if="flag.type == 'boolean'"
                                @toggle="toggleTarget"
                                :label="
                                    (rule?.description ?? 'New rule') +
                                    ' served value'
                                "
                                :current-value="modals.update.data.value.data"
                                :false-value="falseValue"
                                :true-value="trueValue"
                            />

                            <json-target-toggle
                                v-else-if="flag.type == 'json'"
                                @toggle="toggleTarget"
                                :current-value="modals.update.data.value.data"
                                :values="values"
                            />

                            <string-target-toggle
                                v-else
                                @toggle="toggleTarget"
                                :current-value="modals.update.data.value.data"
                                :values="values"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div
            v-if="isViewMode && rule !== null"
            class="min-w-0 flex-1 px-3 py-3"
        >
            <div class="space-y-3 text-sm">
                <div
                    class="flex flex-col gap-2 md:flex-row md:items-start md:justify-between"
                >
                    <div class="space-y-1.5">
                        <div class="flex flex-wrap items-center gap-2">
                            <div
                                class="text-sm font-medium text-taupe-900 sm:text-base"
                            >
                                {{ rule.description }}
                            </div>
                            <span
                                class="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ring-1"
                                :class="
                                    isCurrentTargetValue
                                        ? 'bg-amber-100 text-amber-800 ring-amber-200'
                                        : 'bg-sky-100 text-sky-800 ring-sky-200'
                                "
                            >
                                {{
                                    isCurrentTargetValue
                                        ? 'Matches current target'
                                        : 'Overrides target'
                                }}
                            </span>
                        </div>
                    </div>

                    <div
                        v-if="!readOnly"
                        class="flex shrink-0 items-center gap-1 rounded-md p-0.5 self-start"
                    >
                        <zen-table-icon
                            @click="toggleUpdateMode"
                            :srText="'Update ' + rule.description + ' Rule'"
                            tooltipText="Update"
                        >
                            <zen-icon-update />
                        </zen-table-icon>
                        <zen-table-icon
                            @click="openRemoveModal"
                            :srText="'Remove ' + rule.description + ' Rule'"
                            tooltipText="Remove"
                        >
                            <zen-icon-remove />
                        </zen-table-icon>
                    </div>
                </div>

                <div class="grid gap-2.5 lg:grid-cols-[minmax(0,1fr)_13rem]">
                    <div
                        class="rounded-md border border-taupe-200 bg-white p-3 text-taupe-800"
                    >
                        <div
                            class="text-[11px] font-medium uppercase tracking-[0.16em] text-taupe-500"
                        >
                            Criteria
                        </div>
                        <div
                            v-if="ruleCriteria"
                            data-testid="rule-criteria"
                            class="mt-2.5 flex flex-wrap items-baseline gap-1.5 sm:gap-2"
                        >
                            <div
                                class="whitespace-nowrap font-medium text-taupe-900"
                            >
                                {{ formatSelector(ruleCriteria.selector) }}
                            </div>
                            <div
                                v-if="ruleCriteria.selector == 'attribute'"
                                class="inline-flex items-center rounded-full bg-taupe-100 px-2 py-0.5 text-[11px] font-medium whitespace-nowrap text-taupe-700"
                            >
                                {{ ruleCriteria.sub_selector }}
                            </div>
                            <div
                                class="whitespace-nowrap font-medium italic text-taupe-600"
                            >
                                {{ formatComparer(ruleCriteria.comparer) }}
                            </div>

                            <div
                                v-if="ruleCriteria.selector == 'context'"
                                data-testid="rule-criteria-values"
                                class="flex min-w-0 flex-wrap gap-1.5 sm:gap-2"
                            >
                                <div
                                    v-for="context in getContextDisplayValues(
                                        ruleCriteria
                                    )"
                                    :key="context.ulid || context.identifier"
                                    class="inline-flex items-center gap-1.5 rounded-full bg-taupe-100 px-2.5 py-1"
                                >
                                    <span
                                        v-if="context.type"
                                        class="border-r border-taupe-300 pr-1.5 text-[11px] font-mono text-taupe-500"
                                    >
                                        {{ context.type }}
                                    </span>
                                    <span>
                                        {{ context.name }}
                                    </span>
                                </div>
                            </div>

                            <div
                                v-if="ruleCriteria.selector == 'segment'"
                                data-testid="rule-criteria-values"
                                class="flex min-w-0 flex-wrap gap-1.5 sm:gap-2"
                            >
                                <div
                                    v-for="segment in getSegmentDisplayValues(
                                        ruleCriteria
                                    )"
                                    :key="segment.ulid"
                                    class="inline-flex items-center rounded-full bg-taupe-100 px-2.5 py-1"
                                >
                                    {{ segment.name }}
                                </div>
                            </div>

                            <div
                                v-if="
                                    ruleCriteria.selector == 'attribute' &&
                                    ruleCriteria.comparer != 'isnull' &&
                                    ruleCriteria.comparer != 'notnull'
                                "
                                data-testid="rule-criteria-values"
                                class="flex min-w-0 flex-wrap gap-1.5 sm:gap-2"
                            >
                                <div
                                    v-for="value in getAttributeDisplayValues(
                                        ruleCriteria
                                    )"
                                    :key="value.key"
                                    class="inline-flex items-center rounded-full bg-taupe-100 px-2.5 py-1"
                                >
                                    {{ value.value }}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div
                        class="rounded-md border border-taupe-200 bg-white p-3"
                    >
                        <div
                            class="text-[11px] font-medium uppercase tracking-[0.16em] text-taupe-500"
                        >
                            Served Value
                        </div>
                        <div
                            data-testid="rule-served-value"
                            class="mt-2.5 font-mono text-sm font-medium text-taupe-900 wrap-anywhere"
                        >
                            {{
                                ruleValueDisplay != null &&
                                ruleValueDisplay !== ''
                                    ? ruleValueDisplay
                                    : '(empty)'
                            }}
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <remove-target-rule-modal
            :is-open="modals.remove.show"
            @close="toggleRemoveModal"
            @confirm="confirmRemove"
        />
    </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import BooleanTargetToggle from '@/screens/_components/project/BooleanTargetToggle.vue'
import StringTargetToggle from '@/screens/_components/project/StringTargetToggle.vue'
import JsonTargetToggle from '@/screens/_components/project/JsonTargetToggle.vue'
import RemoveTargetRuleModal from '@/screens/_components/project/modals/RemoveTargetRuleModal.vue'

import ContextService from '@/services/context.service'
import EnvironmentContextAttributeService from '@/services/environment-context-attribute.service'
import EnvironmentContextAttributeValueService from '@/services/environment-context-attribute-value.service'
import TargetRuleService from '@/services/target-rule.service'
import SegmentService from '@/services/segment.service'
import { normalizeCriteriaForSubmission } from '@/utils/target-rule-criteria'
import {
    getContextDisplayValues,
    getAttributeDisplayValues,
    getSegmentDisplayValues,
    formatComparer,
} from '@/utils/target-rule-display'

import { errorMessage } from '@/utils/notifications'

const props = defineProps({
    rule: {
        type: Object,
        default: null,
    },
    draggable: {
        type: Boolean,
        default: false,
    },
    flag: {
        type: Object,
        required: true,
    },
    readOnly: {
        type: Boolean,
        default: false,
    },
    target: {
        type: Object,
        default: null,
    },
    draftTarget: {
        type: Object,
        default: null,
    },
    trueValue: {
        type: Object,
        default: null,
    },
    falseValue: {
        type: Object,
        default: null,
    },
    values: {
        type: Array,
        default: () => [],
    },
})

const emit = defineEmits(['cancelled', 'removed', 'created', 'updated'])

const route = useRoute()

const modals = reactive({
    update: {
        data: null,
        attributes: {
            raw: [],
            filtered: [],
        },
        values: {
            raw: [],
            filtered: [],
        },
    },
    remove: {
        show: false,
    },
})

const mode = ref('view')

const allowMultiple = computed(() => {
    return (
        modals.update.data.criteria.comparer == 'in' ||
        modals.update.data.criteria.comparer == 'notin'
    )
})

const showManualMultipleAttributeValueInput = computed(() => {
    if (!modals.update.data || !allowMultiple.value) {
        return false
    }

    return (
        modals.update.data.criteria.selector === 'attribute' &&
        modals.update.values.raw.length === 0
    )
})

const manualMultipleAttributeValues = computed({
    get() {
        if (!modals.update.data) {
            return ''
        }

        const values = modals.update.data.criteria.values
        if (Array.isArray(values)) {
            return values
                .map((value) => {
                    if (typeof value === 'string') {
                        return value
                    }

                    if (
                        value &&
                        typeof value === 'object' &&
                        value.value !== undefined
                    ) {
                        return String(value.value)
                    }

                    return ''
                })
                .filter((value) => value !== '')
                .join(', ')
        }

        if (typeof values === 'string') {
            return values
        }

        return ''
    },
    set(value) {
        if (!modals.update.data) {
            return
        }

        modals.update.data.criteria.values = value
            .split(',')
            .map((entry) => entry.trim())
            .filter((entry) => entry !== '')
    },
})

const isCreateMode = computed(() => mode.value == 'create')
const isUpdateMode = computed(() => mode.value == 'update')
const isViewMode = computed(() => mode.value == 'view')

const isDraggable = computed(() => props.draggable)

// The rule/target props default to null and their value/criteria relations can
// be absent at runtime (ZEN-1012); type-aware linting infers them as
// non-nullish from the `type: Object` prop declarations, so the optional
// chains below are flagged as unnecessary when they are not.
const isCurrentTargetValue = computed(() => {
    // eslint-disable-next-line
    const ruleValueUlid = props.rule?.value?.data?.ulid
    // eslint-disable-next-line
    const targetValueUlid = props.target?.value?.data?.ulid
    return ruleValueUlid != null && ruleValueUlid == targetValueUlid
})

// eslint-disable-next-line
const ruleCriteria = computed(() => props.rule?.criteria?.data?.[0] ?? null)

const ruleValueDisplay = computed(
    // eslint-disable-next-line
    () => props.rule?.value?.data?.display ?? null
)

const comparerList = computed(() => {
    if (!modals.update.data) return []
    switch (modals.update.data.criteria.selector) {
        case 'attribute':
            return [
                { value: 'equal', name: formatComparer('equal') },
                { value: 'notequal', name: formatComparer('notequal') },
                { value: 'in', name: formatComparer('in') },
                { value: 'notin', name: formatComparer('notin') },
                { value: 'gt', name: formatComparer('gt') },
                { value: 'gte', name: formatComparer('gte') },
                { value: 'lt', name: formatComparer('lt') },
                { value: 'lte', name: formatComparer('lte') },
                { value: 'contains', name: formatComparer('contains') },
                { value: 'notcontains', name: formatComparer('notcontains') },
                { value: 'startswith', name: formatComparer('startswith') },
                {
                    value: 'notstartswith',
                    name: formatComparer('notstartswith'),
                },
                { value: 'endswith', name: formatComparer('endswith') },
                { value: 'notendswith', name: formatComparer('notendswith') },
                { value: 'isnull', name: formatComparer('isnull') },
                { value: 'notnull', name: formatComparer('notnull') },
            ]
        case 'context':
        case 'segment':
            return [
                { value: 'equal', name: formatComparer('equal') },
                { value: 'notequal', name: formatComparer('notequal') },
                { value: 'in', name: formatComparer('in') },
                { value: 'notin', name: formatComparer('notin') },
            ]
    }
    return []
})

const selectorList = computed(() => {
    return [
        { value: 'context', name: formatSelector('context') },
        { value: 'attribute', name: formatSelector('attribute') },
        { value: 'segment', name: formatSelector('segment') },
    ]
})

function cancelCreate() {
    emit('cancelled')
}

function changeAttribute() {
    clearValues()
}

function changeSelector() {
    clearValues()
    reloadValues()
}

function changeComparer() {
    clearValues()
}

function clearValues() {
    modals.update.data.criteria.values = allowMultiple.value ? [] : null
}

function confirmRemove() {
    TargetRuleService.remove(
        route.params.projectKey,
        route.params.environmentKey,
        props.rule.target_ulid,
        props.rule.ulid
    ).then(
        () => {
            toggleRemoveModal()
            emit('removed', props.rule.ulid)
        },
        (error) => {
            errorMessage(error)
        }
    )
}

function confirmCreate() {
    const criteria = normalizeCriteriaForSubmission(modals.update.data.criteria)

    TargetRuleService.create(
        route.params.projectKey,
        route.params.environmentKey,
        props.draftTarget.ulid,
        modals.update.data.value.data.ulid,
        modals.update.data.description,
        criteria
    )
        .then((response) => {
            emit('created', response.data)
        })
        .catch((error) => {
            errorMessage(error)
        })
}

function confirmUpdate() {
    const criteria = normalizeCriteriaForSubmission(modals.update.data.criteria)

    TargetRuleService.update(
        route.params.projectKey,
        route.params.environmentKey,
        props.rule.target_ulid,
        props.rule.ulid,
        modals.update.data.value.data.ulid,
        modals.update.data.description,
        criteria
    ).then(
        (response) => {
            toggleViewMode()
            emit('updated', response.data)
        },
        (error) => {
            errorMessage(error)
        }
    )
}

function formatSelector(value) {
    switch (value) {
        case 'attribute':
            return 'Context Attribute'
        case 'context':
            return 'Context'
        case 'segment':
            return 'Segment'
        default:
            return '(Unknown)'
    }
}

function openRemoveModal() {
    toggleRemoveModal()
}

function reloadAttributes() {
    EnvironmentContextAttributeService.list(
        route.params.projectKey,
        route.params.environmentKey
    )
        .then((data) => {
            modals.update.attributes.raw = data
            modals.update.attributes.filtered = data
        })
        .catch((error) => {
            errorMessage(error)
        })
}

function reloadValues() {
    if (!modals.update.data) return
    switch (modals.update.data.criteria.selector) {
        case 'attribute':
            EnvironmentContextAttributeValueService.list(
                route.params.projectKey,
                route.params.environmentKey
            )
                .then((data) => {
                    modals.update.values.raw = data
                    modals.update.values.filtered = data
                })
                .catch((error) => {
                    errorMessage(error)
                })
            break

        case 'context':
            ContextService.lookup(
                route.params.projectKey,
                route.params.environmentKey
            )
                .then((data) => {
                    modals.update.values.raw = data
                    modals.update.values.filtered = data
                })
                .catch((error) => {
                    errorMessage(error)
                })
            break

        case 'segment':
            SegmentService.lookup(
                route.params.projectKey,
                route.params.environmentKey
            )
                .then((data) => {
                    modals.update.values.raw = data
                    modals.update.values.filtered = data
                })
                .catch((error) => {
                    errorMessage(error)
                })
            break

        default:
            modals.update.values.raw = []
            modals.update.values.filtered = []
            break
    }
}

function setUpdateValues(criteria) {
    let values = null
    switch (criteria.selector) {
        case 'attribute':
            values = criteria.values
            break

        case 'context':
            if (criteria.values && criteria.values.data.length > 0) {
                const contextByIdentifier = (
                    criteria.contexts?.data || []
                ).reduce((carry, context) => {
                    carry[context.identifier] = context
                    return carry
                }, {})

                values = {
                    data: criteria.values.data.map((value) => {
                        const identifier = value.value
                        const existing = contextByIdentifier[identifier]

                        if (existing) {
                            return {
                                ...existing,
                                identifier,
                            }
                        }

                        return {
                            identifier,
                            value: identifier,
                        }
                    }),
                }
            } else {
                values = criteria.contexts
            }
            break

        case 'segment':
            values = criteria.segments
            break

        default:
            return []
    }

    if (!values) {
        return []
    }

    if (criteria.comparer === 'in' || criteria.comparer === 'notin') {
        return values.data
    }

    return values.data[0]
}

function toggleUpdateMode() {
    const criteria = ruleCriteria.value ?? {}

    modals.update.data = {
        description: props.rule.description,
        criteria: {
            comparer: criteria.comparer,
            selector: criteria.selector,
            sub_selector: criteria.sub_selector,
            values: setUpdateValues(criteria),
        },
        value: props.rule.value,
    }

    mode.value = 'update'

    reloadAttributes()
    reloadValues()
}

function toggleTarget(value) {
    modals.update.data.value.data = value
}

function toggleViewMode() {
    mode.value = 'view'
}

function toggleRemoveModal() {
    modals.remove.show = !modals.remove.show
}

onMounted(() => {
    if (props.rule == null) {
        mode.value = 'create'
        modals.update.data = {
            description: null,
            criteria: {
                selector: 'context',
                selector_sub: null,
                comparer: 'equal',
                values: null,
            },
            value: {
                data: props.falseValue,
            },
        }

        reloadAttributes()
        reloadValues()
    }
})
</script>
